/**
 * Centralised SEO metadata for every page on stylon.co.
 * Titles are keyword-optimised; descriptions are 150-160 chars.
 * JSON-LD arrays follow schema.org specs.
 */

/* The company's own accounts, defined once — see socialProfiles.js for why. */
import { SOCIAL_PROFILES } from './socialProfiles';

const BASE = 'https://stylon.co';

// ─── Reusable schema fragments ────────────────────────────────────────────────

export const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Stylon',
  legalName: 'Stylon Ltd',
  // Confirmed by the owner 2026-10-14. Wanted by Wikidata and by every engine
  // resolving "which Stylon is this" — see socialProfiles.js for the rest.
  foundingDate: '2021',
  alternateName: ['Stylon Ltd', 'סטיילון', 'ستايلون'],
  url: BASE,
  logo: `${BASE}/logo512.png`,
  image: `${BASE}/og-image.jpg`,
  sameAs: SOCIAL_PROFILES,
  description:
    'Stylon is an all-in-one booking, records and business-management platform for any appointment-based service business. It includes online booking, client CRM, clinical records and dental or body charting, treatment plans, agreements and consents, courses, a product shop with inventory, point of sale, team scheduling, automated SMS and WhatsApp messaging, marketing and ad optimisation, bookable rooms and resources, nightly-rate property rentals, and a built-in booking website. Built for hair salons, barbershops, nail salons, beauty studios, spas, med-spas, massage studios, tanning salons, tattoo studios, fitness studios, pet groomers, dental and medical clinics, aesthetic clinics, online coaches, therapists, tutors, and more. Fully multilingual: English, Arabic, and Hebrew with RTL support.',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Haifa',
    addressCountry: 'IL',
  },
  areaServed: ['IL', 'AE', 'SA', 'JO', 'PS', 'Worldwide'],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'hello@stylon.co',
      telephone: '+972-53-3920842',
      url: `${BASE}/support`,
      availableLanguage: ['English', 'Arabic', 'Hebrew'],
    },
  ],
};

export const SOFTWARE_APP_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Stylon',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Appointment Scheduling Software',
  operatingSystem: 'Web, iOS (PWA), Android (PWA)',
  url: BASE,
  image: `${BASE}/og-image.jpg`,
  description:
    'All-in-one booking, records and management platform for any appointment-based business — salons, barbershops, spas, med-spas, aesthetic and laser clinics, dental practices, physiotherapy and nutrition clinics, training academies, villas and vacation rentals, fitness and tattoo studios, pet groomers, online coaches and more. Multilingual (English, Arabic, Hebrew).',
  /* NO `offers` HERE. It said `price: '0', priceCurrency: 'USD'` and "Free for
   * the first 6 months during early access" — a hardcoded Offer of zero, which
   * tells Google the product is free, on a page that never reads the API. The
   * price is defined in the system and can be changed there (4 active plans,
   * priceMode GROSS, a 14-day trial, and no six-month free period anywhere),
   * so the only page entitled to state it is /pricing/, which fetches it.
   * See src/seo/__tests__/onlyThePageThatReadsTheSystemStatesAPrice.test.js. */
  featureList: [
    'Online booking page (24/7)',
    'Clinical and client records built from a business-defined field library',
    'Dental charting on a standards-based FDI chart, per tooth and per surface',
    'Body treatment-area mapping with per-area history and derived visit codes',
    'Injectables facial map with 23 regions, units per region and photographs',
    'Treatment plans across visits, with plan money and staged sequences',
    'Agreements and consents scoped per treatment, signed on screen and rendered to PDF',
    'Courses with intakes, per-session seats, materials, fees and certificates',
    'Product shop with per-variant stock and pricing, and barcode scanning',
    'Point of sale with mixed cash, card and wallet settlement',
    'Automatic ad optimisation over a connected Meta ad account',
    'Nightly-rate property rentals with stays and a per-property ledger',
    'Rooms, chairs and equipment as bookable resources',
    'Group and multi-person bookings solved for one fitting time',
    'WhatsApp and SMS inbox with segment broadcasts',
    'Client CRM with full booking history',
    'Team and staff management',
    'Automated SMS and WhatsApp reminders',
    'Service catalogue, packages, and discounts',
    'Calendar with day, week, month, and agenda views',
    'Branded booking website builder',
    'QR code generator for the booking page',
    'Zoom integration for online consultations',
    'Multilingual: English, Arabic, Hebrew (RTL)',
    'Mobile PWA — installs from the browser, no app store needed',
  ],
};

export const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Stylon',
  url: BASE,
  inLanguage: ['en', 'ar', 'he'],
  potentialAction: {
    '@type': 'SearchAction',
    target: `${BASE}/guides?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const breadcrumb = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map(([name, path], idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name,
    item: `${BASE}${path}`,
  })),
});

// The faqSchema helper was removed on 2026-09-09.
//
// Every page that used it hand-wrote questions that appeared nowhere on the
// page: 21 verticals, 17 feature pages, home, pricing, guides and setup-app.
// Google requires FAQ content to be visible, and feature pages were emitting
// two competing FAQPage blocks as a result. Each page now builds its FAQPage
// from the same translated strings its visible FAQ section renders, so the
// markup cannot describe what a reader cannot find.

// ─── Page metadata ─────────────────────────────────────────────────────────────

export const HOME_META = {
  title: {
    en: 'Stylon — Booking, CRM & Business Management Software',
    he: 'סטיילון — תוכנת הזמנת תורים, CRM וניהול עסק',
    ar: 'ستايلون — برنامج حجز المواعيد، CRM وإدارة الأعمال',
  },
  description: {
    en: 'All-in-one booking and management platform for appointment businesses: salons, barbershops, spas, clinics, fitness and tattoo studios, pet grooming and more.',
    he: 'פלטפורמה אחת להזמנת תורים ולניהול עסק: מספרות, ברברים, ספא, קליניקות, סטודיו כושר וקעקועים, טיפוח לחיות מחמד ועוד.',
    ar: 'منصة واحدة لحجز المواعيد وإدارة العمل: صالونات، حلاقة، سبا، عيادات، استوديوهات لياقة ووشم، عناية بالحيوانات وغير ذلك.',
  },
  canonical: '/',
  // Organization JSON-LD is emitted statically in public/index.html so it appears
  // on every prerendered page as the canonical brand entity. HOME_META adds the
  // page-specific schemas (SoftwareApplication, WebSite, FAQPage) here.
  jsonLd: [
    SOFTWARE_APP_SCHEMA,
    WEBSITE_SCHEMA,
  ],
};

export const FEATURES_META = {
  title: {
    en: 'All Features — Booking & Business Management Platform',
    he: 'כל הפיצ׳רים — פלטפורמת הזמנת תורים וניהול עסק',
    ar: 'جميع الميزات — منصة الحجز وإدارة الأعمال',
  },
  description: {
    en: 'Everything Stylon offers: online booking, client CRM, appointment and team management, payments, a booking website and QR codes — for any appointment business.',
    he: 'כל מה שסטיילון מציעה: הזמנת תורים אונליין, CRM ללקוחות, ניהול תורים וצוות, תשלומים, אתר הזמנות וקוד QR — לכל עסק שמבוסס תורים.',
    ar: 'كل ما تقدمه ستايلون: حجز أونلاين، CRM للعملاء، إدارة المواعيد والفريق، مدفوعات، موقع حجز ورمز QR — لأي عمل قائم على المواعيد.',
  },
  canonical: '/features',
  jsonLd: [
    breadcrumb([['Home', '/'], ['Features', '/features']]),
    // Organization JSON-LD is emitted statically in public/index.html on every page.
  ],
};

// ─── Feature page metadata (keyed by featureKey) ──────────────────────────────

export const FEATURE_META = {
  // /features/marketing shipped with no FEATURE_META entry at all, so a page
  // that is in the sitemap and in the primary nav had no title, description or
  // canonical of its own. It is the same omission that kept it out of the
  // sitemap and the prerender list until 2026-09.
  marketing: {
    title: {
      en: 'Marketing Tools — Campaigns, Reminders, Reviews',
      he: 'כלי שיווק — קמפיינים, תזכורות וביקורות',
      ar: 'أدوات التسويق — حملات وتذكيرات وتقييمات',
    },
    description: {
      en: 'Win back quiet clients, run discount campaigns, collect reviews and reach people on WhatsApp — from the same place your bookings and client records live.',
      he: 'להחזיר לקוחות שנעלמו, להריץ קמפיינים, לאסוף ביקורות ולהגיע לאנשים בוואטסאפ — מאותו מקום שבו נמצאים התורים ורשומות הלקוחות.',
      ar: 'استعِد العملاء الذين انقطعوا، وأدِر حملات الخصومات، واجمع التقييمات، وتواصل عبر واتساب — من المكان نفسه الذي تعيش فيه حجوزاتك وسجلات عملائك.',
    },
    canonical: '/features/marketing',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Marketing', '/features/marketing']])],
  },

  'auto-ads': {
    title: { en: 'Automatic Ad Optimisation — Budget Toward Bookings',
             he: 'אופטימיזציית פרסום אוטומטית — תקציב למה שמייצר תורים',
             ar: 'تحسين إعلاني تلقائي — ميزانية نحو الحجوزات' },
    description: { en: 'Connect Meta, set what a booking is worth, and let the optimiser move budget toward the ads producing appointments — under a ceiling you set.',
                   he: 'מחברים את מטא, קובעים כמה שווה תור, והאופטימיזטור מזיז תקציב למודעות שמייצרות תורים — מתחת לתקרה שקבעתם.',
                   ar: 'اربط ميتا، وحدّد قيمة الحجز، واترك المُحسِّن ينقل الميزانية نحو الإعلانات المُنتجة للمواعيد — تحت سقف تضعه.' },
    canonical: '/features/auto-ads',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Automatic Ads', '/features/auto-ads']])],
  },
  hospitality: {
    title: { en: 'Stays & Property Management — Nightly Rates',
             he: 'ניהול אירוח ונכסים — מחירים ללילה',
             ar: 'إدارة الإقامات والعقارات — أسعار ليلية' },
    description: { en: 'Villas, guest rooms and apartments priced per night, with stays, deposits, a per-property ledger and staff access scoped to each property.',
                   he: 'וילות, חדרי אירוח ודירות שמתומחרים ללילה, עם שהיות, מקדמות, ספר כספים לכל נכס והרשאות צוות לפי נכס.',
                   ar: 'فلل وغرف ضيافة وشقق مُسعّرة بالليلة، مع إقامات ودفعات مقدمة ودفتر لكل عقار وصلاحيات موظفين لكل عقار.' },
    canonical: '/features/hospitality',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Stays', '/features/hospitality']])],
  },
  rooms: {
    title: { en: 'Room & Resource Booking — Rooms, Chairs, Equipment',
             he: 'הזמנת חדרים ומשאבים — חדרים, כיסאות וציוד',
             ar: 'حجز الغرف والموارد — غرف وكراسٍ وأجهزة' },
    description: { en: 'Rooms, chairs and equipment booked alongside the specialist, so availability accounts for space as well as staff. Couples rooms and occupancy included.',
                   he: 'חדרים, כיסאות וציוד נתפסים יחד עם המטפל, כך שהזמינות מחשבת גם מקום וגם צוות. כולל חדרים זוגיים ותפוסה.',
                   ar: 'غرف وكراسٍ وأجهزة تُحجز مع الأخصائي، فتحسب الإتاحة المكان كما تحسب الموظفين. وتشمل غرف الزوجين والشغل.' },
    canonical: '/features/rooms',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Rooms', '/features/rooms']])],
  },
  'group-booking': {
    title: { en: 'Group & Multi-Person Booking — One Time That Fits',
             he: 'הזמנה קבוצתית ורבת-משתתפים — שעה אחת שמתאימה',
             ar: 'الحجز الجماعي ومتعدّد الأشخاص — وقت واحد يناسب' },
    description: { en: 'Several people on one booking, each with their own services and specialist, priced as a group and changeable one guest at a time.',
                   he: 'כמה אנשים בהזמנה אחת, לכל אחד השירותים והמטפל שלו, מתומחר כקבוצה וניתן לשינוי אורח אחד בכל פעם.',
                   ar: 'عدة أشخاص في حجز واحد، لكل منهم خدماته وأخصائيه، مُسعَّرًا كمجموعة وقابلًا للتغيير ضيفًا ضيفًا.' },
    canonical: '/features/group-booking',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Group Booking', '/features/group-booking']])],
  },
  messages: {
    title: { en: 'WhatsApp & SMS for Business — One Client Inbox',
             he: 'ווטסאפ ו-SMS לעסק — תיבה אחת ללקוחות',
             ar: 'واتساب ورسائل نصية للعمل — صندوق عملاء واحد' },
    description: { en: 'WhatsApp and SMS from inside Stylon, with the client history beside the thread. Automatic reminders, replies in one inbox, and segment broadcasts.',
                   he: 'ווטסאפ ו-SMS מתוך סטיילון, עם היסטוריית הלקוח לצד השיחה. תזכורות אוטומטיות, תשובות בתיבה אחת ושידורים לפלח.',
                   ar: 'واتساب ورسائل نصية من داخل ستايلون، وسجل العميل بجانب المحادثة. تذكيرات تلقائية وردود في صندوق واحد وبثّ إلى شريحة.' },
    canonical: '/features/messages',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Messages', '/features/messages']])],
  },

  agreements: {
    title: {
      en: 'Consent Forms & Agreements — Scoped, Signed, PDF',
      he: 'טופסי הסכמה והסכמים — מוגבלים, חתומים, PDF',
      ar: 'نماذج الموافقة والاتفاقيات — محدودة وموقَّعة وPDF',
    },
    description: {
      en: 'Build the agreements your work requires, scope each to the treatments it covers, capture an on-screen signature, and produce the PDF months later.',
      he: 'בונים את ההסכמים שהעבודה דורשת, מגבילים כל אחד לטיפולים שהוא מכסה, לוכדים חתימה על המסך, ומפיקים PDF גם חודשים אחר כך.',
      ar: 'ابنِ الاتفاقيات التي يقتضيها عملك، وحُدّ كل واحدة بعلاجاتها، وخُذ توقيعًا على الشاشة، وأخرِج PDF بعد أشهر.',
    },
    canonical: '/features/agreements',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Agreements', '/features/agreements']])],
  },
  courses: {
    title: {
      en: 'Course Management Software — Intakes & Seats',
      he: 'תוכנה לניהול קורסים — מחזורים, מקומות ותעודות',
      ar: 'برنامج إدارة الدورات — دفعات ومقاعد وشهادات',
    },
    description: {
      en: 'Run courses with their own intakes, per-session seats, materials, fees and completion certificates — for training academies and any business that teaches.',
      he: 'ניהול קורסים עם מחזורים, מקומות לכל מפגש, חומרים, שכר לימוד ותעודות סיום — למכללות הדרכה ולכל עסק שמלמד.',
      ar: 'أدِر دورات بدفعاتها ومقاعدها لكل جلسة وموادها ورسومها وشهادات إتمامها — لأكاديميات التدريب ولكل عمل يعلّم.',
    },
    canonical: '/features/courses',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Courses', '/features/courses']])],
  },
  shop: {
    title: {
      en: 'Shop & Inventory — Variants, Stock, Barcodes',
      he: 'חנות ומלאי — וריאנטים, מלאי וסריקת ברקוד',
      ar: 'المتجر والمخزون — متغيّرات ومخزون ومسح باركود',
    },
    description: {
      en: 'Sell products with real variants and real stock. Price and count per variant, scan barcodes from a phone, and group products into browsable collections.',
      he: 'מכירת מוצרים עם וריאנטים ומלאי אמיתיים. מחיר ומלאי לכל וריאנט, סריקת ברקוד מהטלפון, ואוספים נוחים לעיון.',
      ar: 'بِع منتجات بمتغيّرات ومخزون حقيقيين. سعِّر واحسب لكل متغيّر، وامسح الباركود من الهاتف، وجمّع المنتجات في مجموعات.',
    },
    canonical: '/features/shop',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Shop', '/features/shop']])],
  },
  pos: {
    title: {
      en: 'Point of Sale — Baskets, Split Payments, Cash and Card',
      he: 'קופה — סלים, תשלומים מפוצלים, מזומן וכרטיס',
      ar: 'نقطة البيع — سلات ودفعات مقسومة ونقد وبطاقة',
    },
    description: {
      en: 'Build a basket from services and products, settle in cash, card, wallet or a mix, track what stays outstanding, and reconcile the day.',
      he: 'בונים סל משירותים ומוצרים, סוגרים במזומן, כרטיס, ארנק או שילוב, עוקבים אחרי יתרות פתוחות, ומסכמים את היום.',
      ar: 'ابنِ سلة من الخدمات والمنتجات، وسوِّها نقدًا أو بالبطاقة أو المحفظة أو بخليط، وتابع المستحق، وطابِق اليوم.',
    },
    canonical: '/features/pos',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Point of Sale', '/features/pos']])],
  },

  // ── Published by the 2026-09 expansion ─────────────────────────────────────
  'dental-charting': {
    title: {
      en: 'Dental Charting Software — FDI Chart per Patient',
      he: 'תוכנת רישום שיניים — מפת FDI לכל מטופל',
      ar: 'برنامج تخطيط الأسنان — مخطط FDI لكل مريض',
    },
    description: {
      en: 'A real FDI dental chart in every patient file. Mark teeth and individual surfaces, chart primary dentition, and see every treatment a tooth has had.',
      he: 'מפת שיניים תקנית לפי FDI בכל תיק מטופל. סימון שיניים ומשטחים, שיניים נשירות, וכל טיפול שהשן עברה.',
      ar: 'مخطط أسنان معياري بترقيم FDI في كل ملف مريض. علِّم الأسنان والأسطح، وخطِّط الأسنان اللبنية، وشاهد كل علاج تلقّاه السن.',
    },
    canonical: '/features/dental-charting',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Dental Charting', '/features/dental-charting']])],
  },
  'treatment-areas': {
    title: {
      en: 'Treatment Area Mapping for Laser & Aesthetics',
      he: 'אזורי טיפול בגוף — מיפוי אזורים ללייזר ואסתטיקה',
      ar: 'مناطق العلاج في الجسم — تخطيط المناطق للليزر والتجميل',
    },
    description: {
      en: 'Map services onto body areas so every session is scoped and priced by what was actually treated, with per-area history and derived visit codes.',
      he: 'מיפוי שירותים לאזורי גוף כך שכל טיפול מוגדר ומתומחר לפי מה שבאמת טופל, עם היסטוריה לכל אזור וקודים נגזרים.',
      ar: 'اربط الخدمات بمناطق الجسم لتُحدَّد كل جلسة وتُسعَّر بحسب ما عُولج فعلًا، مع تاريخ لكل منطقة ورموز زيارة مُشتقّة.',
    },
    canonical: '/features/treatment-areas',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Treatment Areas', '/features/treatment-areas']])],
  },
  injectables: {
    title: {
      en: 'Injectables Software — Facial Map, Units & Consent',
      he: 'תוכנה להזרקות — מפת פנים, יחידות והסכמות',
      ar: 'برنامج الحقن — خريطة الوجه والوحدات والموافقات',
    },
    description: {
      en: 'Record what went where across 23 facial regions, with units per region, before and after photographs, and a consent scoped to each treatment.',
      he: 'תיעוד מה הוזרק איפה על 23 אזורי פנים, עם יחידות לכל אזור, תמונות לפני ואחרי, והסכמה לכל טיפול.',
      ar: 'سجّل ما أُعطي وأين عبر 23 منطقة في الوجه، مع وحدات لكل منطقة وصور ما قبل وما بعد وموافقة لكل علاج.',
    },
    canonical: '/features/injectables',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Injectables', '/features/injectables']])],
  },
  records: {
    title: {
      en: 'Client & Patient Records — Your Own Field Library',
      he: 'רשומות לקוח ומטופל — ספריית שדות משלכם',
      ar: 'سجلات العملاء والمرضى — مكتبة حقول خاصة بك',
    },
    description: {
      en: 'Decide what every record asks. Structured fields you define, file attachments, standalone patient files and role-based visibility.',
      he: 'החליטו מה כל רשומה שואלת. שדות מובנים שאתם מגדירים, צירוף קבצים, קבצי מטופל עצמאיים והרשאות לפי תפקיד.',
      ar: 'قرّر ما يسأله كل سجل. حقول منظَّمة تحدّدها، ومرفقات ملفات، وملفات مرضى مستقلّة، وظهور بحسب الدور.',
    },
    canonical: '/features/records',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Records', '/features/records']])],
  },
  'treatment-plans': {
    title: {
      en: 'Treatment Plans — Multi-Visit Courses & Plan Money',
      he: 'תוכניות טיפול — מהלכים מרובי ביקורים וכספים',
      ar: 'خطط العلاج — مسارات متعددة الزيارات ومالية الخطة',
    },
    description: {
      en: 'Lay out a course of treatment across appointments, target teeth or body areas per item, and track what it costs against what has been paid.',
      he: 'פריסת מהלך טיפול על פני תורים, יעד של שן או אזור לכל פריט, ומעקב אחרי עלות מול תשלום.',
      ar: 'افرد مسار العلاج على عدة مواعيد، واستهدف سنًّا أو منطقةً لكل عنصر، وتابع التكلفة مقابل ما دُفع.',
    },
    canonical: '/features/treatment-plans',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Treatment Plans', '/features/treatment-plans']])],
  },

  booking: {
    title: {
      en: 'Online Booking System for Salons & Businesses',
      he: 'מערכת הזמנת תורים אונליין לעסקים',
      ar: 'نظام حجز المواعيد أونلاين للصالونات والأعمال',
    },
    description: {
      en: 'Let clients book 24/7 with a custom booking page. Instant confirmations, no double-bookings, multilingual support for any service business.',
      he: 'לקוחות קובעים תור 24/7 בעמוד הזמנות משלכם. אישור מיידי, בלי כפל תורים, ותמיכה בעברית, ערבית ואנגלית לכל עסק שירות.',
      ar: 'يحجز عملاؤك على مدار الساعة من صفحة حجز خاصة بك. تأكيد فوري، بلا ازدواج مواعيد، ودعم بالعربية والعبرية والإنجليزية.',
    },
    canonical: '/features/online-booking',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Online Booking', '/features/online-booking']]),
    ],
  },
  appointments: {
    title: {
      en: 'Appointment Management Software for Salons',
      he: 'תוכנה לניהול תורים לעסקים',
      ar: 'برنامج إدارة المواعيد للصالونات',
    },
    description: {
      en: 'Manage every appointment in one place. Approve, reject, reschedule and track appointments across your whole team with full calendar control.',
      he: 'כל התורים במקום אחד: אישור, דחייה, שינוי מועד ומעקב אחרי כל אנשי הצוות, עם שליטה מלאה ביומן.',
      ar: 'كل المواعيد في مكان واحد: قبول ورفض وتغيير موعد ومتابعة لكل أفراد الفريق، مع تحكم كامل بالتقويم.',
    },
    canonical: '/features/appointments',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Appointments', '/features/appointments']]),
    ],
  },
  crm: {
    title: {
      en: 'Salon CRM — Client Management System',
      he: 'CRM לעסק — ניהול לקוחות וכרטיס לקוח',
      ar: 'نظام CRM لإدارة عملاء صالونك',
    },
    description: {
      en: 'Track every client\'s booking history, notes and preferences automatically. Build loyalty, spot trends, and grow repeat bookings with Stylon\'s salon CRM.',
      he: 'היסטוריית ביקורים, העדפות והערות לכל לקוח. דעו מי חוזר, מי נעלם ומי שווה פנייה — בלי אקסל.',
      ar: 'سجل زيارات وتفضيلات وملاحظات لكل عميل. اعرف من يعود ومن انقطع ومن يستحق المتابعة — بلا جداول.',
    },
    canonical: '/features/crm',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Client CRM', '/features/crm']]),
    ],
  },
  notifications: {
    title: {
      en: 'Automated Appointment Notifications & Confirmations',
      he: 'אישורי תור והתראות אוטומטיות ללקוחות',
      ar: 'تأكيدات وإشعارات المواعيد تلقائياً',
    },
    description: {
      en: 'Send automatic booking confirmations and SMS/WhatsApp notifications to clients. Reduce no-shows and keep clients informed with zero manual work.',
      he: 'אישור הזמנה אוטומטי והתראות ב-SMS וב-WhatsApp לכל שינוי בתור. פחות אי-הגעות, בלי עבודה ידנית.',
      ar: 'تأكيد حجز تلقائي وإشعارات عبر SMS وواتساب. غياب أقل وعملاء على اطّلاع، بلا عمل يدوي.',
    },
    canonical: '/features/notifications',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Notifications', '/features/notifications']]),
    ],
  },
  website: {
    title: {
      en: 'Free Salon Website & Booking Page Builder',
      he: 'אתר ועמוד הזמנות לעסק — חינם',
      ar: 'موقع وصفحة حجز لعملك — مجاناً',
    },
    description: {
      en: 'Get a professional, multilingual booking website for your salon in minutes. Branded design, custom domain, no code required — fully included with Stylon.',
      he: 'אתר הזמנות רב-לשוני ומעוצב לעסק שלכם תוך דקות: מיתוג משלכם, דומיין אישי, בלי שורת קוד.',
      ar: 'موقع حجز متعدد اللغات بهويتك خلال دقائق: شعارك وألوانك، نطاق خاص، وبلا أي برمجة.',
    },
    canonical: '/features/website',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Website Builder', '/features/website']]),
    ],
  },
  profile: {
    title: {
      en: 'Customise Your Salon Booking Page',
      he: 'עיצוב עמוד ההזמנות של העסק',
      ar: 'خصّص صفحة الحجز الخاصة بعملك',
    },
    description: {
      en: 'Brand your booking page with your logo, cover photo, colour theme, gallery, and opening hours. Professional design live in minutes — no designer needed.',
      he: 'לוגו, תמונת נושא, צבעים, גלריה ושעות פתיחה — עמוד הזמנות מקצועי באוויר תוך דקות, בלי מעצב.',
      ar: 'شعارك وصورة الغلاف والألوان والمعرض وساعات العمل — صفحة حجز احترافية خلال دقائق، بلا مصمم.',
    },
    canonical: '/features/profile',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Booking Page', '/features/profile']]),
    ],
  },
  zoom: {
    title: {
      en: 'Online Appointments with Zoom Integration',
      he: 'פגישות אונליין עם חיבור ל-Zoom',
      ar: 'مواعيد أونلاين مع تكامل Zoom',
    },
    description: {
      en: 'Offer online consultations with built-in Zoom integration. Zoom meeting links are generated automatically when a virtual service is booked — no manual setup.',
      he: 'ייעוץ ופגישות מרחוק עם קישור Zoom שנוצר אוטומטית בכל הזמנת שירות אונליין — בלי הגדרה ידנית.',
      ar: 'استشارات وجلسات عن بُعد مع رابط Zoom يُنشأ تلقائياً عند حجز خدمة أونلاين — بلا إعداد يدوي.',
    },
    canonical: '/features/zoom',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Zoom Integration', '/features/zoom']]),
    ],
  },
  reminders: {
    title: {
      en: 'Smart Appointment Reminders — Reduce No-Shows',
      he: 'תזכורות לתורים — פחות אי-הגעות',
      ar: 'تذكيرات المواعيد — غياب أقل',
    },
    description: {
      en: 'Cut no-shows with automatic SMS & WhatsApp reminders before appointments. Smart reminders also re-engage past clients to drive repeat bookings.',
      he: 'תזכורות אוטומטיות ב-SMS וב-WhatsApp לפני התור, ותזכורות חכמות שמחזירות לקוחות שלא הגיעו מזמן.',
      ar: 'تذكيرات تلقائية عبر SMS وواتساب قبل الموعد، وتذكيرات ذكية تعيد العملاء الذين غابوا طويلاً.',
    },
    canonical: '/features/reminders',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Reminders', '/features/reminders']]),
    ],
  },
  team: {
    title: {
      en: 'Salon Team & Staff Management Software',
      he: 'ניהול צוות ועובדים בעסק',
      ar: 'إدارة الفريق والموظفين في صالونك',
    },
    description: {
      en: 'Manage your entire team from one dashboard. Set individual schedules, assign services, track appointments per specialist, and control access levels.',
      he: 'כל הצוות בלוח אחד: שעות עבודה לכל איש צוות, שיוך שירותים, מעקב תורים והרשאות גישה.',
      ar: 'كل الفريق في لوحة واحدة: ساعات عمل لكل موظف، ربط الخدمات، متابعة المواعيد وصلاحيات الدخول.',
    },
    canonical: '/features/team',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Team Management', '/features/team']]),
    ],
  },
  discounts: {
    title: {
      en: 'Salon Discount Campaigns & Promotional Offers',
      he: 'מבצעים וקמפיינים של הנחות לעסק',
      ar: 'عروض وحملات خصومات لصالونك',
    },
    description: {
      en: 'Drive bookings with targeted discount campaigns. Set percentage or fixed discounts, date ranges, service targets, and per-customer usage limits.',
      he: 'הנחה באחוזים או בסכום, טווח תאריכים, שירותים נבחרים והגבלת שימוש ללקוח — קמפיין שמביא תורים.',
      ar: 'خصم بنسبة أو بمبلغ، فترة محددة، خدمات مختارة وحد استخدام لكل عميل — حملة تجلب حجوزات.',
    },
    canonical: '/features/discounts',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Discounts', '/features/discounts']]),
    ],
  },
  payments: {
    title: {
      en: 'Salon Payment & Service Pricing Management',
      he: 'תמחור שירותים ומעקב תשלומים',
      ar: 'تسعير الخدمات ومتابعة المدفوعات',
    },
    description: {
      en: 'Set and display service prices, starting-from rates, or hide pricing for quote-only services. Track payment status per appointment with ease.',
      he: 'מחיר לכל שירות, מחיר "החל מ-", או הסתרת מחיר לשירות לפי הצעה — ומעקב תשלום לכל תור.',
      ar: 'سعر لكل خدمة، أو "ابتداءً من"، أو إخفاء السعر للخدمات حسب الطلب، ومتابعة حالة الدفع لكل موعد.',
    },
    canonical: '/features/payments',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Payments', '/features/payments']]),
    ],
  },
  gallery: {
    title: {
      en: 'Salon Work Gallery — Showcase Your Best Work',
      he: 'גלריית עבודות לעמוד ההזמנות',
      ar: 'معرض أعمالك في صفحة الحجز',
    },
    description: {
      en: 'Upload and organise your portfolio on your public booking page. Let photos sell your services before clients even book — drag to reorder, add captions.',
      he: 'העלו תיק עבודות לעמוד ההזמנות הציבורי: גרירה לסידור, כיתובים, ותמונות שמוכרות עוד לפני ההזמנה.',
      ar: 'ارفع أعمالك إلى صفحة الحجز العامة: ترتيب بالسحب، تعليقات على الصور، وصور تبيع قبل الحجز.',
    },
    canonical: '/features/gallery',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Gallery', '/features/gallery']]),
    ],
  },
  services: {
    title: {
      en: 'Manage Salon Services, Categories & Packages',
      he: 'ניהול שירותים, קטגוריות וחבילות',
      ar: 'إدارة الخدمات والفئات والباقات',
    },
    description: {
      en: 'Create service categories, set prices, durations, and visibility. Bundle services into packages clients can book. Control everything from one place.',
      he: 'קטגוריות שירות, מחירים, משכי זמן ונראות בעמוד ההזמנות. חבילות שאפשר להזמין — הכול ממקום אחד.',
      ar: 'فئات للخدمات، أسعار، مدد زمنية وظهور في صفحة الحجز. وباقات يمكن حجزها — كل ذلك من مكان واحد.',
    },
    canonical: '/features/services',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Services', '/features/services']]),
    ],
  },
  calendar: {
    title: {
      en: 'Salon Calendar & Schedule Management',
      he: 'יומן ולוח זמנים לעסק',
      ar: 'تقويم وجدول مواعيد لعملك',
    },
    description: {
      en: 'View and manage your full team schedule in Day, Week, Month, or Agenda view. Create and reschedule appointments directly from the calendar.',
      he: 'יומן יומי, שבועי, חודשי או רשימה לכל הצוות. קביעת תור ושינוי מועד ישירות מהיומן.',
      ar: 'عرض يومي وأسبوعي وشهري وقائمة لكل الفريق. أنشئ موعداً أو غيّر وقته مباشرة من التقويم.',
    },
    canonical: '/features/calendar',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Calendar', '/features/calendar']]),
    ],
  },
  roles: {
    title: {
      en: 'Salon Staff Roles & Permission Management',
      he: 'הרשאות ותפקידים לצוות',
      ar: 'الأدوار والصلاحيات لفريق العمل',
    },
    description: {
      en: 'Owner role controls the full business. Specialists manage only their own calendar, clients, and services. Set the right access for every team member.',
      he: 'בעל העסק רואה הכול; איש צוות רואה רק את היומן, הלקוחות והשירותים שלו. הרשאה מדויקת לכל אחד.',
      ar: 'صاحب العمل يرى كل شيء، والموظف يرى تقويمه وعملاءه وخدماته فقط. صلاحية مناسبة لكل شخص.',
    },
    canonical: '/features/roles',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Roles & Permissions', '/features/roles']]),
    ],
  },
  qr: {
    title: {
      en: 'Branded QR Code for Your Salon Booking Page',
      he: 'קוד QR ממותג לעמוד ההזמנות',
      ar: 'رمز QR بهويتك لصفحة الحجز',
    },
    description: {
      en: 'Generate a custom branded QR code for your booking page. Choose your design style and language, download, and place it anywhere clients can scan.',
      he: 'צרו קוד QR מעוצב לעמוד ההזמנות: בחירת סגנון ושפה, הורדה, ותלייה בכל מקום שהלקוחות סורקים.',
      ar: 'أنشئ رمز QR مصمماً لصفحة حجزك: اختر النمط واللغة، نزّله، وضعه في أي مكان يمسحه العملاء.',
    },
    canonical: '/features/qr',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['QR Code', '/features/qr']]),
    ],
  },
  app: {
    title: {
      en: 'Stylon as a Mobile App — Install on iPhone or Android',
      he: 'סטיילון כאפליקציה — iPhone ואנדרואיד',
      ar: 'ستايلون كتطبيق — آيفون وأندرويد',
    },
    description: {
      en: 'Install Stylon as a PWA on iPhone or Android — no app store download needed. Manage your salon from any device with the full portal experience.',
      he: 'התקינו את סטיילון כאפליקציה ב-iPhone או באנדרואיד בלי חנות אפליקציות, ונהלו את העסק מכל מכשיר.',
      ar: 'ثبّت ستايلون كتطبيق على آيفون أو أندرويد بلا متجر تطبيقات، وأدر عملك من أي جهاز.',
    },
    canonical: '/features/app',
    jsonLd: [
      breadcrumb([['Home', '/'], ['Features', '/features'], ['Mobile App', '/features/app']]),
    ],
  },
  ai: {
    title: {
      en: 'AI Business Assistant for Salons — Coming Soon',
      he: 'עוזר AI לעסק — בקרוב',
      ar: 'مساعد ذكاء اصطناعي لعملك — قريباً',
    },
    description: {
      en: 'Stylon AI will answer questions, take actions, and give data-driven recommendations to grow your business. Coming soon.',
      he: 'סטיילון AI יענה על שאלות, יבצע פעולות וייתן המלצות מבוססות נתונים לצמיחת העסק. בקרוב.',
      ar: 'سيجيب ستايلون AI عن أسئلتك، وينفّذ المهام، ويقدّم توصيات مبنية على بياناتك لنمو عملك. قريباً.',
    },
    canonical: '/features/ai',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['AI Assistant', '/features/ai']])],
  },
  finance: {
    title: {
      en: 'Salon Finance, Invoices & Payment Reports',
      he: 'חשבוניות, קבלות ודוחות הכנסה לעסק',
      ar: 'الفواتير والإيصالات وتقارير الدخل',
    },
    description: {
      en: 'Issue invoices and receipts, take deposits, apply cancellation policies, and see what every day, specialist and service actually earned.',
      he: 'הפקת חשבוניות וקבלות, גביית מקדמות, מדיניות ביטול, ודוח הכנסות לפי יום, איש צוות ושירות.',
      ar: 'أصدر فواتير وإيصالات، واقبض عربوناً، وطبّق سياسة إلغاء، وراجع دخل كل يوم وكل موظف وكل خدمة.',
    },
    canonical: '/features/finance',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Finance', '/features/finance']])],
  },
  googlecal: {
    title: {
      en: 'Google Calendar Sync for Salons — Coming Soon',
      he: 'סנכרון עם Google Calendar — בקרוב',
      ar: 'مزامنة مع تقويم Google — قريباً',
    },
    description: {
      en: 'Sync your Stylon appointments with Google Calendar automatically. Never miss a booking across your personal and business calendars. Coming soon.',
      he: 'סנכרון אוטומטי של התורים מסטיילון ליומן Google. בלי לפספס תור בין היומן האישי לעסקי. בקרוב.',
      ar: 'مزامنة تلقائية لمواعيد ستايلون مع تقويم Google. لا يفوتك موعد بين تقويمك الشخصي والعملي. قريباً.',
    },
    canonical: '/features/google-calendar',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Google Calendar', '/features/google-calendar']])],
  },
  reviews: {
    title: {
      en: 'Client Reviews & Ratings for Your Business',
      he: 'ביקורות ודירוגים מלקוחות',
      ar: 'تقييمات ومراجعات العملاء',
    },
    description: {
      en: 'Clients rate the appointment they just had and leave a review. Show real ratings on your booking page and see what each specialist scores.',
      he: 'לקוחות מדרגים את התור שהיה וכותבים ביקורת. הדירוגים מוצגים בעמוד ההזמנות, עם פילוח לפי איש צוות.',
      ar: 'يقيّم العملاء الموعد الذي حضروه ويكتبون مراجعة. تظهر التقييمات في صفحة الحجز، مع تفصيل لكل موظف.',
    },
    canonical: '/features/reviews',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Reviews', '/features/reviews']])],
  },
  products: {
    title: {
      en: 'Sell Products Online Alongside Your Services',
      he: 'מכירת מוצרים אונליין לצד השירותים',
      ar: 'بيع المنتجات أونلاين إلى جانب خدماتك',
    },
    description: {
      en: 'List retail products on your booking page with photos, variants, stock and delivery. Take orders and payment in the same place clients book.',
      he: 'חנות בעמוד ההזמנות: תמונות, וריאציות, מלאי ומשלוח. הזמנה ותשלום באותו מקום שבו קובעים תור.',
      ar: 'متجر في صفحة الحجز: صور ومتغيّرات ومخزون وتوصيل. اطلب وادفع في المكان نفسه الذي يُحجز منه.',
    },
    canonical: '/features/products',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Products Store', '/features/products']])],
  },
  ads: {
    // /features/ads redirects to /features/marketing, so this never renders.
    // Kept in step with reality anyway: the feature shipped as
    // /features/auto-ads, so "coming soon" was stale, and the old title
    // marketed Stylon as salon software.
    title: {
      en: 'Ad Optimisation for Appointment Businesses',
      he: 'ניהול קמפיינים ממומנים לעסקי תורים',
      ar: 'إدارة الإعلانات المموّلة لأعمال المواعيد',
    },
    description: {
      en: 'Connect your Meta ad account and let Stylon move budget toward the ads that produce bookings rather than clicks, under a spending ceiling you set yourself.',
      he: 'חברו את חשבון המודעות של Meta, וסטיילון יעביר תקציב למודעות שמביאות תורים — עד תקרה שאתם קובעים.',
      ar: 'اربط حساب إعلانات Meta، وينقل ستايلون الميزانية إلى الإعلانات التي تجلب حجوزات — ضمن سقف تحدده.',
    },
    canonical: '/features/ads',
    jsonLd: [breadcrumb([['Home', '/'], ['Features', '/features'], ['Smart Ads', '/features/ads']])],
  },
};

// ─── Business category page metadata ─────────────────────────────────────────

export const CATEGORY_META = {
  laser: {
    title: { en: 'Laser Clinic Software — Price by Treatment Area',
             he: 'תוכנה למרפאות לייזר — הזמנה ותמחור לפי אזור טיפול',
             ar: 'برنامج عيادات الليزر — حجز وتسعير بمنطقة العلاج' },
    description: { en: 'Run a laser clinic on body-area mapping: price each session by the areas treated, see per-area history, and capture consent per treatment.',
                   he: 'ניהול מרפאת לייזר על מיפוי אזורי גוף: תמחור לפי האזורים שטופלו, היסטוריה לכל אזור, והסכמה לכל טיפול.',
                   ar: 'أدِر عيادة ليزر على تخطيط مناطق الجسم: سعِّر كل جلسة بمناطقها، وشاهد تاريخ كل منطقة، وخُذ موافقة لكل علاج.' },
    canonical: '/for/laser-hair-removal',
    jsonLd: [breadcrumb([['Home', '/'], ['Industries', '/for'], ['Laser Clinics', '/for/laser-hair-removal']])],
  },
  aesthetic: {
    title: { en: 'Aesthetic Clinic Software — Facial Map, Units, Consent',
             he: 'תוכנה למרפאות אסתטיקה — מפת פנים, יחידות והסכמות',
             ar: 'برنامج عيادات التجميل — خريطة الوجه والوحدات والموافقات' },
    description: { en: 'Record injectables against 23 facial regions with units, before-and-after photographs on the region, and a consent scoped to each treatment.',
                   he: 'תיעוד הזרקות על 23 אזורי פנים עם יחידות, תמונות לפני ואחרי על האזור, והסכמה לכל טיפול.',
                   ar: 'سجّل الحقن مقابل 23 منطقة في الوجه بالوحدات، وصور ما قبل وما بعد على المنطقة، وموافقة لكل علاج.' },
    canonical: '/for/aesthetic-clinics',
    jsonLd: [breadcrumb([['Home', '/'], ['Industries', '/for'], ['Aesthetic Clinics', '/for/aesthetic-clinics']])],
  },
  academies: {
    title: { en: 'Training Academy Software — Courses & Intakes',
             he: 'תוכנה למכללות הדרכה — קורסים, מחזורים ותעודות',
             ar: 'برنامج أكاديميات التدريب — دورات ودفعات وشهادات' },
    description: { en: 'Run courses with their own intakes and per-session seats, share materials with the enrolled, track fees per student and issue certificates.',
                   he: 'ניהול קורסים עם מחזורים ומקומות לכל מפגש, שיתוף חומרים עם הרשומים, מעקב שכר לימוד ותעודות.',
                   ar: 'أدِر دورات بدفعاتها ومقاعدها لكل جلسة، وشارِك المواد مع المسجّلين، وتابع الرسوم وأصدِر الشهادات.' },
    canonical: '/for/academies',
    jsonLd: [breadcrumb([['Home', '/'], ['Industries', '/for'], ['Training Academies', '/for/academies']])],
  },
  rentals: {
    title: { en: 'Villa & Vacation Rental Software — Nightly Rates',
             he: 'תוכנה לווילות והשכרות חופשה — מחירים ללילה',
             ar: 'برنامج الفلل والإيجارات السياحية — أسعار ليلية' },
    description: { en: 'Let villas and apartments by the night with seasonal rate ladders, stays rather than appointments, deposits and a ledger per property.',
                   he: 'השכרת וילות ודירות ללילה עם סולמות מחירי עונה, שהיות ולא תורים, מקדמות וספר כספים לכל נכס.',
                   ar: 'أجِّر الفلل والشقق بالليلة بسلالم أسعار موسمية، وإقامات لا مواعيد، ودفعات مقدمة ودفتر لكل عقار.' },
    canonical: '/for/vacation-rentals',
    jsonLd: [breadcrumb([['Home', '/'], ['Industries', '/for'], ['Villas & Rentals', '/for/vacation-rentals']])],
  },
  physio: {
    title: { en: 'Physiotherapy Software — Records, Plans, Body Areas',
             he: 'תוכנה לפיזיותרפיה — רשומות, תוכניות ואזורי גוף',
             ar: 'برنامج العلاج الطبيعي — سجلات وخطط ومناطق الجسم' },
    description: { en: 'Assessment fields you design yourself, treatment plans across visits with body-area targets, and standalone files for referrals and imaging.',
                   he: 'שדות אבחון שאתם מעצבים, תוכניות טיפול על פני ביקורים עם יעדי אזור, וקבצים עצמאיים להפניות ולדימות.',
                   ar: 'حقول تقييم تصمّمها، وخطط علاج على مدى الزيارات بأهداف في مناطق الجسم، وملفات مستقلّة للإحالات والتصوير.' },
    canonical: '/for/physiotherapy',
    jsonLd: [breadcrumb([['Home', '/'], ['Industries', '/for'], ['Physiotherapy', '/for/physiotherapy']])],
  },
  nutrition: {
    title: { en: 'Nutrition & Dietetics Software — Intake, Plans, Zoom',
             he: 'תוכנה לתזונה ודיאטה — קליטה, תוכניות וזום',
             ar: 'برنامج التغذية والحميات — استقبال وخطط وزوم' },
    description: { en: 'Design your own intake, run consultations in person or over Zoom from one calendar, and sell multi-week programmes with their money tracked.',
                   he: 'עיצוב קליטה משלכם, ייעוצים פרונטלית או בזום מיומן אחד, ומכירת תוכניות רבות שבועות עם מעקב כספים.',
                   ar: 'صمّم استقبالك، وأدِر الاستشارات حضوريًّا أو عبر زوم من تقويم واحد، وبِع برامج متعددة الأسابيع بمتابعة ماليتها.' },
    canonical: '/for/nutrition',
    jsonLd: [breadcrumb([['Home', '/'], ['Industries', '/for'], ['Nutrition', '/for/nutrition']])],
  },

  hair: {
    title: {
      en: 'Salon Management Software for Hair Salons',
      he: 'תוכנת ניהול מספרות — הזמנת תורים, לקוחות וצוות',
      ar: 'برنامج إدارة صالونات الشعر — حجز وعملاء وفريق',
    },
    description: {
      en: 'Stylon helps hair salons manage bookings, clients, staff, and pricing in one platform. Custom booking page, automated reminders, and team scheduling included.',
      he: 'סטיילון עוזרת למספרות לנהל הזמנות, לקוחות, צוות ומחירים בפלטפורמה אחת. דף הזמנות מותאם אישית, תזכורות אוטומטיות וניהול לוח זמנים לצוות — הכל בפנים.',
      ar: 'تساعد ستايلون صالونات الشعر على إدارة الحجوزات والعملاء والفريق والأسعار في منصة واحدة. صفحة حجز مخصصة وتذكيرات تلقائية وجدولة الفريق مشمولة.',
    },
    canonical: '/for/hair-salons',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Hair Salons', '/for/hair-salons']]),
    ],
  },
  nails: {
    title: {
      en: 'Nail Salon Booking & Management Software',
      he: 'תוכנת הזמנת תורים וניהול לסטודיו ציפורניים',
      ar: 'برنامج حجز وإدارة صالون أظافر',
    },
    description: {
      en: 'Run your nail salon smarter. Online booking, client management, team scheduling, discount campaigns, and a branded website — all in Stylon.',
      he: 'נהלו את סטודיו הציפורניים שלכם בחוכמה. הזמנת תורים אונליין, ניהול לקוחות, ניהול צוות, מבצעי הנחות ואתר ממותג — הכל בסטיילון.',
      ar: 'أدر صالون الأظافر الخاص بك بذكاء. حجز أونلاين، إدارة العملاء، تنظيم الفريق، حملات الخصومات، وموقع مخصص بهويتك — كل ذلك في ستايلون.',
    },
    canonical: '/for/nail-salons',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Nail Salons', '/for/nail-salons']]),
    ],
  },
  spa: {
    title: {
      en: 'Spa Management Software & Online Booking',
      he: 'תוכנת ניהול ספא והזמנת תורים אונליין',
      ar: 'برنامج إدارة السبا وحجز المواعيد أونلاين',
    },
    description: {
      en: 'Streamline your spa with Stylon. Online booking, staff management, client profiles, automated reminders, payments, and a branded booking page.',
      he: 'ייעלו את הספא שלכם עם סטיילון. הזמנת תורים אונליין, ניהול צוות, פרופילי לקוחות, תזכורות אוטומטיות, תשלומים, ודף הזמנות ממותג.',
      ar: 'بسّط إدارة السبا الخاص بك مع ستايلون. حجز أونلاين، إدارة الفريق، ملفات العملاء، تذكيرات تلقائية، مدفوعات، وصفحة حجز مخصصة بهويتك.',
    },
    canonical: '/for/spa',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Spa', '/for/spa']]),
    ],
  },
  dental: {
    title: {
      en: 'Dental Practice Software — FDI Charting',
      he: 'תוכנה למרפאות שיניים — רישום FDI ותיקי מטופלים',
      ar: 'برنامج عيادات الأسنان — تخطيط FDI وسجلات المرضى',
    },
    description: {
      en: 'Chart teeth and surfaces on a real FDI chart in every patient file, plan treatment by tooth, capture consent per procedure, and book recalls that bring patients back.',
      he: 'נהלו תורים, רשומות מטופלים ולוח זמנים של הצוות עם סטיילון. תזכורות אוטומטיות, הזמנה אונליין, ותמיכה בכמה רופאים.',
      ar: 'أدر مواعيد الأسنان وسجلات المرضى وجداول الفريق مع ستايلون. تذكيرات تلقائية، حجز أونلاين، ودعم لعدة أطباء.',
    },
    canonical: '/for/dental',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Dental Clinics', '/for/dental']]),
    ],
  },
  clinics: {
    title: {
      en: 'Clinic Software — Patient Records, Plans & Consents',
      he: 'תוכנה לקליניקות — רשומות מטופלים, תוכניות והסכמות',
      ar: 'برنامج العيادات — سجلات المرضى والخطط والموافقات',
    },
    description: {
      en: 'Patient records built from your own field library, treatment plans across visits, body and facial mapping where the work needs it, and consents scoped per treatment.',
      he: 'פשטו את התפעול של הקליניקה שלכם עם סטיילון. הזמנת תורים אונליין, פרופילי מטופלים, תזכורות אוטומטיות לתורים, תיאום צוות וניהול לוח שנה מלא.',
      ar: 'بسّط عمليات عيادتك مع ستايلون. حجز أونلاين، ملفات المرضى، تذكيرات تلقائية للمواعيد، تنسيق الفريق، وإدارة كاملة للتقويم.',
    },
    canonical: '/for/clinics',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Clinics', '/for/clinics']]),
    ],
  },
  online: {
    title: {
      en: 'Online Consultation & Session Booking with Zoom',
      he: 'הזמנת פגישות אונליין עם Zoom',
      ar: 'حجز الجلسات أونلاين مع Zoom',
    },
    description: {
      en: 'Perfect for coaches, therapists, tutors, and remote service providers. Book online sessions with automatic Zoom links — clients get the link instantly.',
      he: 'מתאים למאמנים, מטפלים, מורים ונותני שירות מרחוק: פגישה אונליין עם קישור Zoom שנשלח מיד ללקוח.',
      ar: 'مناسب للمدربين والمعالجين والمعلمين ومقدّمي الخدمات عن بُعد: جلسة أونلاين برابط Zoom يصل العميل فوراً.',
    },
    canonical: '/for/online',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Online Services', '/for/online']]),
    ],
  },
  other: {
    title: {
      en: 'Appointment Booking Software for Any Service Business',
      he: 'תוכנת הזמנת תורים לכל עסק שירות',
      ar: 'برنامج حجز مواعيد لأي عمل خدمي',
    },
    description: {
      en: 'Stylon works for any appointment-based business. Custom booking page, client CRM, team management, automated reminders, and payments — all in one.',
      he: 'סטיילון מתאים לכל עסק שמבוסס תורים: עמוד הזמנות, כרטיס לקוח, ניהול צוות, תזכורות ותשלומים.',
      ar: 'ستايلون يناسب أي عمل قائم على المواعيد: صفحة حجز، ملف عميل، إدارة فريق، تذكيرات ومدفوعات.',
    },
    canonical: '/for/other',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Other Businesses', '/for/other']]),
    ],
  },
  barber: {
    title: {
      en: 'Barbershop Booking & Management Software',
      he: 'תוכנת הזמנת תורים וניהול לברבר ומספרת גברים',
      ar: 'برنامج حجز وإدارة محلات الحلاقة (باربر شوب)',
    },
    description: {
      en: 'Run your barbershop with Stylon: online booking, walk-in handling, client CRM, multi-barber scheduling, automated SMS & WhatsApp reminders, and a branded booking website.',
      he: 'נהלו את הברברשופ שלכם עם סטיילון: הזמנת תורים אונליין, טיפול ב-walk-ins, CRM ללקוחות, ניהול לוח זמנים למספר ברברים, תזכורות אוטומטיות בסמס ובוואטסאפ, ואתר הזמנות ממותג.',
      ar: 'أدر محل الحلاقة مع ستايلون: حجز أونلاين، زبائن بلا موعد، CRM للعملاء، جدولة لعدة حلاقين، تذكيرات SMS وواتساب، وموقع حجز بهويتك.',
    },
    canonical: '/for/barber-shops',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Barber Shops', '/for/barber-shops']]),
    ],
  },
  massage: {
    title: {
      en: 'Massage Therapy Booking & Scheduling Software',
      he: 'תוכנה לניהול והזמנת עיסויים',
      ar: 'برنامج حجز وإدارة جلسات المساج',
    },
    description: {
      en: 'Stylon helps massage studios and independent therapists run online booking, manage client history, schedule treatments, and send automated reminders — all in one platform.',
      he: 'סטודיו עיסוי ומטפלים עצמאיים: הזמנה אונליין, היסטוריית טיפולים ללקוח, לוח זמנים ותזכורות אוטומטיות.',
      ar: 'لاستوديوهات المساج والمعالجين المستقلين: حجز أونلاين، سجل جلسات لكل عميل، جدولة وتذكيرات تلقائية.',
    },
    canonical: '/for/massage',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Massage Studios', '/for/massage']]),
    ],
  },
  medspa: {
    title: {
      en: 'Med-Spa Software — Facial & Body Mapping, Packages',
      he: 'תוכנה למד-ספא — מיפוי פנים וגוף וחבילות',
      ar: 'برنامج المنتجعات الطبية — تخطيط الوجه والجسم والحِزم',
    },
    description: {
      en: 'Record treatment against facial regions and body areas with units and photographs, capture consent per treatment, and track packages against usage and payment.',
      he: 'תיעוד טיפול מול אזורי פנים וגוף עם יחידות ותמונות, הסכמה לכל טיפול, ומעקב חבילות מול ניצול ותשלום.',
      ar: 'سجّل العلاج مقابل مناطق الوجه والجسم بالوحدات والصور، وخُذ موافقة لكل علاج، وتابع الحِزم مقابل الاستهلاك والدفع.',
    },
    canonical: '/for/med-spa',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Med-Spas', '/for/med-spa']]),
    ],
  },
  fitness: {
    title: {
      en: 'Fitness Studio & Personal Trainer Booking Software',
      he: 'תוכנת הזמנות לסטודיו כושר ולמאמנים',
      ar: 'برنامج حجز لاستوديوهات اللياقة والمدربين',
    },
    description: {
      en: 'Stylon helps fitness studios and personal trainers manage class bookings, private sessions, client history, payments and team schedules — with a branded booking page included.',
      he: 'סטודיו כושר ומאמנים אישיים: הזמנת שיעורים ואימונים אישיים, היסטוריית לקוח, תשלומים ולוח צוות.',
      ar: 'لاستوديوهات اللياقة والمدربين الشخصيين: حجز الحصص والجلسات الخاصة، سجل العميل، المدفوعات وجدول الفريق.',
    },
    canonical: '/for/fitness',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Fitness Studios', '/for/fitness']]),
    ],
  },
  tanning: {
    title: {
      en: 'Tanning Salon Booking & Management Software',
      he: 'תוכנה לניהול מכון שיזוף',
      ar: 'برنامج إدارة صالون التسمير',
    },
    description: {
      en: 'Run your tanning salon with Stylon: online booking by service or bed, client history, package deals, automated reminders, and a branded booking page — all in one.',
      he: 'מכון שיזוף: הזמנה לפי שירות או מיטה, היסטוריית לקוח, חבילות, תזכורות אוטומטיות ועמוד הזמנות ממותג.',
      ar: 'صالون تسمير: حجز حسب الخدمة أو السرير، سجل العميل، باقات، تذكيرات تلقائية وصفحة حجز بهويتك.',
    },
    canonical: '/for/tanning',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Tanning Salons', '/for/tanning']]),
    ],
  },
  tattoo: {
    title: {
      en: 'Tattoo Studio Booking & Artist Management Software',
      he: 'תוכנה לניהול סטודיו קעקועים',
      ar: 'برنامج إدارة استوديو الوشم',
    },
    description: {
      en: 'Stylon helps tattoo studios manage artist schedules, consultations, deposits, custom service pricing, client galleries, and a branded booking page — all in one platform.',
      he: 'סטודיו קעקועים: יומן לכל אמן, פגישות ייעוץ, מקדמות, תמחור לפי עבודה, גלריית לקוח ועמוד הזמנות ממותג.',
      ar: 'استوديو وشم: تقويم لكل فنان، جلسات استشارة، عرابين، تسعير حسب العمل، معرض صور وصفحة حجز بهويتك.',
    },
    canonical: '/for/tattoo',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Tattoo Studios', '/for/tattoo']]),
    ],
  },
  pet: {
    title: {
      en: 'Pet Grooming Booking & Scheduling Software',
      he: 'תוכנה להזמנת תורים לטיפוח חיות מחמד',
      ar: 'برنامج حجز وإدارة العناية بالحيوانات الأليفة',
    },
    description: {
      en: 'Run your pet grooming business with Stylon: online booking by service, client and pet profiles, automated reminders, payments, and a branded booking website.',
      he: 'עסקי טיפוח לחיות: הזמנה לפי שירות, כרטיס לקוח וחיה, תזכורות אוטומטיות, תשלומים ואתר הזמנות.',
      ar: 'أعمال العناية بالحيوانات: حجز حسب الخدمة، ملف للعميل وحيوانه، تذكيرات تلقائية، مدفوعات وموقع حجز.',
    },
    canonical: '/for/pet-grooming',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Pet Grooming', '/for/pet-grooming']]),
    ],
  },
  beauty: {
    title: {
      en: 'Beauty Studio Booking & Management Software',
      he: 'תוכנת הזמנת תורים וניהול לסטודיו יופי',
      ar: 'برنامج حجز وإدارة استوديو التجميل',
    },
    description: {
      en: "Manage your beauty studio with Stylon: online booking, client CRM, team scheduling, service & package pricing, automated reminders, and a branded booking website — all in one.",
      he: 'נהלו את סטודיו היופי שלכם עם סטיילון: הזמנת תורים אונליין, CRM ללקוחות, ניהול צוות, תמחור שירותים וחבילות, תזכורות אוטומטיות ואתר הזמנות ממותג — הכל במקום אחד.',
      ar: 'أدر استوديو التجميل الخاص بك مع ستايلون: حجز أونلاين، CRM للعملاء، تنظيم الفريق، تسعير الخدمات والباقات، تذكيرات تلقائية، وموقع حجز مخصص بهويتك — كل ذلك في مكان واحد.',
    },
    canonical: '/for/beauty',
    jsonLd: [
      breadcrumb([['Home', '/'], ['For Businesses', '/for'], ['Beauty Studios', '/for/beauty']]),
    ],
  },
};

// ─── Other pages ──────────────────────────────────────────────────────────────

export const BUSINESSES_OVERVIEW_META = {
  title: {
    en: 'Stylon for Your Business — Any Appointment-Based Industry',
    he: 'סטיילון לעסק שלך — לכל ענף שמבוסס על תורים',
    ar: 'ستايلون لعملك — لأي صناعة قائمة على المواعيد',
  },
  description: {
    en: 'See how Stylon works for salons, barbershops, nail and beauty studios, spas, massage and tattoo studios, fitness, pet grooming, dental and aesthetic clinics. Start free.',
    he: 'איך סטיילון עובדת למספרות, ברברים, מניקור, סטודיו יופי, ספא, עיסויים, קעקועים, כושר, טיפוח חיות וקליניקות שיניים ואסתטיקה. התחילו בחינם.',
    ar: 'كيف تعمل ستايلون لصالونات الشعر والحلاقة والأظافر والتجميل والسبا والمساج والوشم واللياقة والعناية بالحيوانات وعيادات الأسنان والتجميل. ابدأ مجاناً.',
  },
  canonical: '/for',
  jsonLd: [
    breadcrumb([['Home', '/'], ['For Businesses', '/for']]),
    // Organization JSON-LD is emitted statically in public/index.html on every page.
  ],
};

/* The page's own title and description say what the page IS, not what it
 * costs: the plans are fetched from the system at render time, and a title
 * baked at build time cannot track a price that can change. It used to read
 * "Pricing — Free for 6 Months", an offer the system has never carried. */
export const PRICING_META = {
  title: {
    en: 'Pricing & Plans',
    he: 'מחירון ותוכניות',
    ar: 'الأسعار والخطط',
  },
  description: {
    en: 'Every Stylon plan on one page: what each includes, how many specialists and messages it covers, and the monthly or yearly price. Start with a free trial, no credit card.',
    he: 'כל התוכניות של סטיילון בעמוד אחד: מה כל אחת כוללת, כמה מטפלים והודעות, והמחיר החודשי או השנתי. מתחילים בתקופת ניסיון, בלי כרטיס אשראי.',
    ar: 'جميع خطط ستايلون في صفحة واحدة: ما تشمله كل خطة، عدد المختصين والرسائل، والسعر الشهري أو السنوي. ابدأ بفترة تجريبية بدون بطاقة ائتمان.',
  },
  canonical: '/pricing',
  jsonLd: [
    breadcrumb([['Home', '/'], ['Pricing', '/pricing']]),
  ],
};

export const ABOUT_META = {
  title: {
    en: 'About Stylon — Our Story & Mission',
    he: 'אודות סטיילון — הסיפור והמטרה שלנו',
    ar: 'عن ستايلون — قصتنا ورسالتنا',
  },
  description: {
    en: 'Stylon was built to help beauty professionals and service businesses focus on their craft, not their admin. Learn about our mission and the team behind it.',
    he: 'סטיילון נבנתה כדי שבעלי מקצוע ועסקי שירות יתעסקו במה שהם עושים הכי טוב, ולא בניירת. הכירו את המטרה שלנו ואת הצוות שמאחוריה.',
    ar: 'بُنيت ستايلون ليتفرّغ أصحاب المهن وأعمال الخدمات لما يتقنونه، لا للأعمال الورقية. تعرّف على رسالتنا وعلى الفريق الذي يقف خلفها.',
  },
  canonical: '/about',
  jsonLd: [
    breadcrumb([['Home', '/'], ['About', '/about']]),
    // Organization JSON-LD is emitted statically in public/index.html on every page.
  ],
};

export const GUIDES_META = {
  title: {
    en: 'Help Guides & Tutorials',
    he: 'מדריכים והדרכות לשימוש בסטיילון',
    ar: 'أدلة الاستخدام والشروحات',
  },
  description: {
    en: 'Step-by-step guides for every Stylon feature. Learn to set up online booking, manage appointments, build your team, create discounts, and more.',
    he: 'מדריכים שלב אחר שלב לכל יכולת בסטיילון: הקמת הזמנת תורים, ניהול תורים, הוספת אנשי צוות, יצירת מבצעים ועוד.',
    ar: 'أدلة خطوة بخطوة لكل ميزة في ستايلون: إعداد الحجز أونلاين، إدارة المواعيد، إضافة الفريق، إنشاء العروض والمزيد.',
  },
  canonical: '/guides',
  jsonLd: [
    breadcrumb([['Home', '/'], ['Guides', '/guides']]),
  ],
};

export const SETUP_APP_META = {
  title: {
    en: 'How to Install Stylon as an App',
    he: 'איך מתקינים את סטיילון כאפליקציה',
    ar: 'كيف تثبّت ستايلون كتطبيق',
  },
  description: {
    en: 'Install Stylon on your iPhone or Android in 4 simple steps — no app store download needed. Works as a PWA with full portal functionality.',
    he: 'התקינו את סטיילון ב-iPhone או באנדרואיד בארבעה צעדים, בלי חנות אפליקציות. אפליקציית PWA עם כל יכולות הפורטל.',
    ar: 'ثبّت ستايلون على آيفون أو أندرويد في أربع خطوات، بلا متجر تطبيقات. تطبيق PWA بكامل إمكانات البوابة.',
  },
  canonical: '/setup-app',
  jsonLd: [
    breadcrumb([['Home', '/'], ['Setup App', '/setup-app']]),
  ],
};

/**
 * Legal, support and integration pages.
 *
 * These eight mounted no <SEO> at all, so 24 URLs (8 pages x 3 languages) sat
 * in the sitemap with no canonical, no hreflang and no description. Three
 * language variants of the same document with nothing telling a crawler they
 * are the same document is a duplicate-content problem we were asking for.
 *
 * The policy pages stay indexable — they are legitimate, and an AI assistant
 * asked "what is Stylon's refund policy" should be able to find and quote the
 * real one rather than infer it.
 */
export const STATIC_PAGE_META = {
  'terms-of-use': {
    title: { en: 'Terms of Use', he: 'תנאי שימוש', ar: 'شروط الاستخدام' },
    description: {
      en: 'The terms that govern use of Stylon by businesses and their clients, including accounts, bookings, payments, cancellations and acceptable use.',
      he: 'התנאים החלים על השימוש בסטיילון על ידי עסקים ולקוחותיהם — חשבונות, תורים, תשלומים, ביטולים ושימוש מותר.',
      ar: 'الشروط التي تحكم استخدام ستايلون من قِبل الأعمال وعملائها، وتشمل الحسابات والحجوزات والمدفوعات والإلغاءات والاستخدام المقبول.',
    },
    canonical: '/terms-of-use',
  },
  'privacy-policy': {
    title: { en: 'Privacy Policy', he: 'מדיניות פרטיות', ar: 'سياسة الخصوصية' },
    description: {
      en: 'What personal data Stylon collects from businesses and their clients, why it is held, how long it is kept, and the rights you have over it.',
      he: 'אילו נתונים אישיים סטיילון אוספת מעסקים ומלקוחותיהם, לשם מה הם נשמרים, לכמה זמן, ואילו זכויות יש לכם עליהם.',
      ar: 'ما البيانات الشخصية التي تجمعها ستايلون من الأعمال وعملائها، ولماذا تُحفظ، وكم تبقى، وما حقوقك عليها.',
    },
    canonical: '/privacy-policy',
  },
  'refund-policy': {
    title: { en: 'Cancellation & Refund Policy', he: 'מדיניות ביטול והחזרים', ar: 'سياسة الإلغاء والاسترداد' },
    description: {
      en: 'How cancellations and refunds work on Stylon, including subscription cancellations, deposits taken at booking and refunds owed to clients.',
      he: 'איך עובדים ביטולים והחזרים בסטיילון — ביטול מנוי, מקדמות שנגבו בהזמנה והחזרים המגיעים ללקוחות.',
      ar: 'كيف تعمل الإلغاءات والمبالغ المستردة في ستايلون، بما في ذلك إلغاء الاشتراك والدفعات المقدمة والمبالغ المستحقة للعملاء.',
    },
    canonical: '/refund-policy',
  },
  'payment-policy': {
    title: { en: 'Payment Policy', he: 'מדיניות תשלומים', ar: 'سياسة الدفع' },
    description: {
      en: 'How payments are taken and settled through Stylon: card and cash handling, deposits, tax documents, and which payments a business is responsible for.',
      he: 'איך תשלומים נגבים ומסולקים דרך סטיילון: כרטיס ומזומן, מקדמות, מסמכי מס, ועל אילו תשלומים העסק אחראי.',
      ar: 'كيف تُقبض المدفوعات وتُسوّى عبر ستايلون: البطاقة والنقد والدفعات المقدمة والمستندات الضريبية ومسؤولية العمل عنها.',
    },
    canonical: '/payment-policy',
  },
  aup: {
    title: { en: 'Acceptable Use Policy', he: 'מדיניות שימוש מותר', ar: 'سياسة الاستخدام المقبول' },
    description: {
      en: 'What businesses may and may not do with Stylon, including messaging rules, content standards and how misuse of the platform is handled.',
      he: 'מה מותר ומה אסור לעסקים לעשות בסטיילון — כללי הודעות, תקני תוכן, ואיך מטופל שימוש לרעה בפלטפורמה.',
      ar: 'ما يجوز للأعمال فعله وما لا يجوز في ستايلون، بما في ذلك قواعد المراسلة ومعايير المحتوى ومعالجة إساءة الاستخدام.',
    },
    canonical: '/aup',
  },
  cookies: {
    title: { en: 'Cookie Policy', he: 'מדיניות עוגיות', ar: 'سياسة ملفات تعريف الارتباط' },
    description: {
      en: 'Which cookies Stylon sets, what each one is for, which are strictly necessary, and how to control them from your own browser.',
      he: 'אילו עוגיות סטיילון מציבה, לשם מה כל אחת, אילו הכרחיות, ואיך לשלוט בהן מהדפדפן שלכם.',
      ar: 'ما ملفات تعريف الارتباط التي تضعها ستايلون، وغرض كل منها، وأيها ضروري، وكيف تتحكم بها من متصفحك.',
    },
    canonical: '/cookies',
  },
  support: {
    title: { en: 'Support & Contact', he: 'תמיכה ויצירת קשר', ar: 'الدعم والتواصل' },
    description: {
      en: 'Reach the Stylon team by WhatsApp, phone or email, and find answers on setting up your account, bookings, payments and your booking page.',
      he: 'ליצור קשר עם צוות סטיילון בוואטסאפ, בטלפון או במייל, ולמצוא תשובות על הקמת חשבון, תורים, תשלומים ודף ההזמנות.',
      ar: 'تواصل مع فريق ستايلون عبر واتساب أو الهاتف أو البريد، وجد إجابات عن إعداد حسابك والحجوزات والمدفوعات وصفحة حجزك.',
    },
    canonical: '/support',
  },
  'zoom-integration': {
    title: { en: 'Zoom Integration', he: 'אינטגרציית זום', ar: 'تكامل زووم' },
    description: {
      en: 'How Stylon connects to Zoom, what the integration accesses, and how meeting links are created automatically for every online appointment.',
      he: 'איך סטיילון מתחברת לזום, למה האינטגרציה ניגשת, ואיך נוצרים קישורי פגישה אוטומטית לכל תור אונליין.',
      ar: 'كيف تتصل ستايلون بزووم، وما الذي يصل إليه التكامل، وكيف تُنشأ روابط الاجتماعات تلقائيًّا لكل موعد إلكتروني.',
    },
    canonical: '/zoom-integration',
  },
};

/* ── The journal ──────────────────────────────────────────────────────────
 *
 * SEO titles, deliberately not the same strings as the on-page H1. A heading
 * is read by someone already on the page; a SERP title is read by someone
 * deciding whether to arrive, and the two jobs want different sentences.
 *
 * Every entry is checked by metaLengths.test.js in all three languages, which
 * is why none of them states a price — see
 * onlyThePageThatReadsTheSystemStatesAPrice.test.js.
 */
export const BLOG_INDEX_META = {
  title: { en: 'The Stylon journal', he: 'הבלוג של סטיילון', ar: 'مدوّنة ستايلون' },
  description: {
    en: 'Practical writing for appointment businesses: filling the diary, getting paid on time, and keeping the customers you already have.',
    he: 'כתיבה מעשית לעסקים שעובדים עם תורים: למלא את היומן, לקבל תשלום בזמן, ולשמור על הלקוחות שכבר יש לכם.',
    ar: 'كتابة عملية للأعمال القائمة على المواعيد: ملء الجدول، والتقاضي في وقته، والحفاظ على الزبائن الذين لديك.',
  },
  canonical: '/blog',
};

export const BLOG_CATEGORY_META = {
  grow: {
    title: { en: 'Growing the business', he: 'להגדיל את העסק', ar: 'تنمية العمل' },
    description: {
      en: 'Filling the diary, bringing people back, and becoming the business your area actually trusts — without adding another tool.',
      he: 'למלא את היומן, להחזיר אנשים, ולהפוך לעסק שבאזור שלכם באמת סומכים עליו — בלי להוסיף עוד כלי.',
      ar: 'ملء الجدول، وإعادة الناس، وأن تصبح العمل الذي يثق به محيطك فعلًا — دون إضافة أداة أخرى.',
    },
    canonical: '/blog/category/grow',
  },
  verticals: {
    title: { en: 'For your trade', he: 'לפי תחום', ar: 'حسب مجالك' },
    description: {
      en: 'What changes when you run a salon, a clinic, a studio or a property — and which parts of the day a system should be handling.',
      he: 'מה משתנה כשמנהלים מספרה, קליניקה, סטודיו או נכס — ואילו חלקים ביום מערכת אמורה לנהל במקומכם.',
      ar: 'ما الذي يتغيّر حين تدير صالونًا أو عيادة أو استوديو أو عقارًا — وأي أجزاء اليوم يفترض بالنظام أن يتكفّل بها.',
    },
    canonical: '/blog/category/verticals',
  },
  compare: {
    title: { en: 'Choosing software', he: 'לבחור תוכנה', ar: 'اختيار البرنامج' },
    description: {
      en: 'What to ask before you move a whole business onto somebody else’s system, and where the ceiling usually turns out to be.',
      he: 'מה כדאי לשאול לפני שמעבירים עסק שלם למערכת של מישהו אחר, ואיפה בדרך כלל מתגלה התקרה.',
      ar: 'ما الذي ينبغي أن تسأل عنه قبل نقل عمل كامل إلى نظام شخص آخر، وأين يتبيّن السقف عادةً.',
    },
    canonical: '/blog/category/compare',
  },
  capabilities: {
    title: { en: 'What Stylon does', he: 'מה סטיילון עושה', ar: 'ما يفعله ستايلون' },
    description: {
      en: 'The parts of a working day a system should be handling for you: deposits, stock, courses, records, reminders and the money.',
      he: 'החלקים ביום עבודה שמערכת אמורה לנהל במקומכם: מקדמות, מלאי, קורסים, תיקים, תזכורות והכסף.',
      ar: 'أجزاء يوم العمل التي يفترض بالنظام أن يتكفّل بها: العرابين، والمخزون، والدورات، والملفات، والتذكيرات، والمال.',
    },
    canonical: '/blog/category/capabilities',
  },
};

export const BLOG_POST_META = {
  'all-in-one-vs-appointment-apps': {
    title: { en: 'Booking app or whole business?', he: 'אפליקציית תורים או כל העסק?', ar: 'تطبيق مواعيد أم العمل كلّه؟' },
    description: {
      en: 'A booking app handles one hour of your week. The deposits, the stock, the courses and the customers who drifted away are where the business actually lives.',
      he: 'אפליקציית תורים מטפלת בשעה אחת בשבוע. המקדמות, המלאי, הקורסים והלקוחות שנעלמו — שם העסק באמת קורה.',
      ar: 'تطبيق المواعيد يتولّى ساعة واحدة من أسبوعك. العرابين والمخزون والدورات والزبائن الذين ابتعدوا هي حيث يعيش العمل فعلًا.',
    },
    canonical: '/blog/all-in-one-vs-appointment-apps',
  },
  'run-a-property-on-one-system': {
    title: { en: 'A villa is not a long appointment', he: 'וילה היא לא תור ארוך', ar: 'الفيلا ليست موعدًا طويلًا' },
    description: {
      en: 'Nights, not hours. Seasonal rates, a deposit at booking and a ledger for each property — what changes when a system understands a stay.',
      he: 'לילות, לא שעות. תעריפים עונתיים, מקדמה בהזמנה וספר חשבונות לכל נכס — מה משתנה כשמערכת מבינה שהייה.',
      ar: 'ليالٍ لا ساعات. أسعار موسمية، وعربون عند الحجز، ودفتر حسابات لكل عقار — ما الذي يتغيّر حين يفهم النظام الإقامة.',
    },
    canonical: '/blog/run-a-property-on-one-system',
  },
  'deposits-and-the-empty-chair': {
    title: { en: 'The chair that stayed empty', he: 'הכיסא שנשאר ריק', ar: 'الكرسي الذي بقي فارغًا' },
    description: {
      en: 'A three-hour colour cancelled the night before is most of a day’s takings. Deposits, cancellation windows and a waitlist that fills the gap.',
      he: 'צבע של שלוש שעות שמתבטל בלילה שלפני הוא רוב ההכנסה של יום. מקדמות, חלונות ביטול ורשימת המתנה שממלאת את החור.',
      ar: 'صبغة من ثلاث ساعات تُلغى في الليلة السابقة هي معظم مدخول يوم. العرابين، ونوافذ الإلغاء، وقائمة انتظار تملأ الفجوة.',
    },
    canonical: '/blog/deposits-and-the-empty-chair',
  },
  "why-whatsapp-is-not-enough": {
    title: { en: "Why WhatsApp is not enough", he: "למה וואטסאפ לא מספיק", ar: "لماذا لا يكفي واتساب" },
    description: {
      en: "WhatsApp is the best place to be reached and the worst place to run a business from. What a conversation forgets, and what to put behind it.",
      he: "וואטסאפ הוא המקום הכי טוב להגיע אליך והכי גרוע לנהל ממנו עסק. מה שיחה שוכחת, ומה לשים מאחוריה.",
      ar: "واتساب أفضل مكان ليصلوا إليك وأسوأ مكان لتدير منه عملك. ما تنساه المحادثة، وما الذي يجب أن يقف خلفها.",
    },
    canonical: '/blog/why-whatsapp-is-not-enough',
  },
  "the-customers-you-already-have": {
    title: { en: "The customers you already have", he: "הלקוחות שכבר יש לך", ar: "الزبائن الذين لديك أصلًا" },
    description: {
      en: "You paid to get them through the door once. Getting them back costs a message. Why people drift away, and how to notice before they are gone.",
      he: "שילמת כדי שייכנסו בדלת פעם אחת. להחזיר אותם עולה הודעה. למה אנשים נעלמים, ואיך לשים לב לפני שהם כבר לא כאן.",
      ar: "دفعتَ لتُدخلهم من الباب مرّة. وإعادتهم تكلّف رسالة. لماذا يبتعد الناس، وكيف تنتبه قبل أن يختفوا.",
    },
    canonical: '/blog/the-customers-you-already-have',
  },
  "a-page-people-trust": {
    title: { en: "A page people trust", he: "עמוד שאנשים סומכים עליו", ar: "صفحة يثق بها الناس" },
    description: {
      en: "Most people decide about you before they ever speak to you. What a stranger checks in twenty seconds, and why a page that answers it books more work.",
      he: "רוב האנשים מחליטים עליך עוד לפני שדיברו איתך. מה זר בודק בעשרים שניות, ולמה עמוד שעונה על זה מביא יותר עבודה.",
      ar: "معظم الناس يحسمون رأيهم فيك قبل أن يكلّموك. ما الذي يتفقّده الغريب في عشرين ثانية، ولماذا تجلب الصفحة التي تجيب عنه عملًا أكثر.",
    },
    canonical: '/blog/a-page-people-trust',
  },
  "what-reviews-are-really-for": {
    title: { en: "What reviews are really for", he: "למה באמת משמשות ביקורות", ar: "ما فائدة التقييمات حقًّا" },
    description: {
      en: "A review is not flattery. It is what a stranger reads instead of trusting you. When to ask, what to do with a bad one, and why the count matters more than the score.",
      he: "ביקורת היא לא מחמאה. היא מה שזר קורא במקום לסמוך עליך. מתי לבקש, מה לעשות עם ביקורת רעה, ולמה הכמות חשובה מהציון.",
      ar: "التقييم ليس مجاملة. إنه ما يقرأه الغريب بدل أن يثق بك. متى تطلبه، وماذا تفعل بالسيّئ منه، ولماذا العدد أهم من الدرجة.",
    },
    canonical: '/blog/what-reviews-are-really-for',
  },
  "a-dental-practice-in-one-place": {
    title: { en: "A dental practice in one place", he: "מרפאת שיניים במקום אחד", ar: "عيادة أسنان في مكان واحد" },
    description: {
      en: "Patient files, treatment plans, recalls and payments in one system instead of four. What a practice gets back when the record follows the patient.",
      he: "תיקי מטופלים, תוכניות טיפול, זימונים חוזרים ותשלומים במערכת אחת במקום בארבע. מה מרפאה מקבלת בחזרה כשהרישום הולך אחרי המטופל.",
      ar: "ملفات المرضى وخطط العلاج والاستدعاءات والمدفوعات في نظام واحد بدل أربعة. ما الذي تستعيده العيادة حين يتبع السجلُّ المريضَ.",
    },
    canonical: '/blog/a-dental-practice-in-one-place',
  },
  "a-treatment-that-takes-three-visits": {
    title: { en: "A treatment that takes three visits", he: "טיפול שלוקח שלושה ביקורים", ar: "علاج يستغرق ثلاث زيارات" },
    description: {
      en: "Some work is not one appointment. It is stages, waits between them and a room that is only needed for part of it. What a diary that understands that gives you back.",
      he: "יש עבודות שהן לא תור אחד. הן שלבים, המתנות ביניהם, וחדר שצריך רק לחלק מהם. מה יומן שמבין את זה מחזיר לך.",
      ar: "بعض الأعمال ليست موعدًا واحدًا. إنها مراحل، وانتظارات بينها، وغرفة لا تلزم إلا لجزء منها. ما الذي تعيده لك مفكّرة تفهم ذلك.",
    },
    canonical: '/blog/a-treatment-that-takes-three-visits',
  },
  "the-cost-of-a-no-show": {
    title: { en: "What a no-show actually costs", he: "כמה באמת עולה אי-הגעה", ar: "كم يكلّف التخلّف عن الموعد فعلًا" },
    description: {
      en: "Work out what last month's empty slots really cost you — then see how much of it a reminder, a confirmation and a waitlist take back.",
      he: "תחשבו את המספר האמיתי — ואז תראו כמה ממנו תזכורת, אישור הגעה ורשימת המתנה מחזירים.",
      ar: "احسب كم كلّفتك المواعيد الفارغة الشهر الماضي — ثم انظر كم منه يستعيده التذكير والتأكيد وقائمة الانتظار.",
    },
    canonical: '/blog/the-cost-of-a-no-show',
  },
  "selling-products-beside-the-chair": {
    title: { en: "Selling products beside the chair", he: "למכור מוצרים לצד הכיסא", ar: "بيع المنتجات إلى جانب الكرسي" },
    description: {
      en: "The shelf behind you is the easiest margin in the business, and the hardest to keep track of. Stock that counts itself, and a shop that sells while you are closed.",
      he: "המדף מאחוריך הוא הרווח הקל ביותר בעסק, והקשה ביותר למעקב. מלאי שסופר את עצמו, וחנות שמוכרת גם כשסגור.",
      ar: "الرفّ خلفك هو أسهل هامش ربح في العمل، وأصعبه متابعةً. مخزون يَعُدّ نفسه، ومتجر يبيع وأنت مغلق.",
    },
    canonical: '/blog/selling-products-beside-the-chair',
  },
  "teaching-what-you-know": {
    title: { en: "Teaching what you know", he: "ללמד את מה שאתה יודע", ar: "أن تُعلّم ما تعرفه" },
    description: {
      en: "A course or a workshop is income that does not depend on your hands being free. What running one actually involves, and what makes it collapse.",
      he: "קורס או סדנה הם הכנסה שלא תלויה בכך שהידיים שלך פנויות. מה באמת כרוך בהעברה שלהם, ומה גורם להם להתפרק.",
      ar: "الدورة أو الورشة دخلٌ لا يتوقّف على كون يديك فارغتين. ما الذي يتطلّبه تشغيلها فعلًا، وما الذي يُفشلها.",
    },
    canonical: '/blog/teaching-what-you-know',
  },
  "a-clinic-that-keeps-its-own-records": {
    title: { en: "A clinic that keeps its own records", he: "מרפאה ששומרת את הרשומות של עצמה", ar: "عيادة تحتفظ بسجلّاتها" },
    description: {
      en: "Consent forms, before-and-after photographs, treatment history and who is due back — kept properly, in one place, and findable in seconds.",
      he: "טופסי הסכמה, תמונות לפני ואחרי, היסטוריית טיפולים ומי אמור לחזור — נשמרים כמו שצריך, במקום אחד, ונמצאים בשניות.",
      ar: "استمارات الموافقة، وصور قبل وبعد، وتاريخ العلاج، ومن حان موعد عودته — محفوظة كما ينبغي، في مكان واحد، وتُوجَد في ثوانٍ.",
    },
    canonical: '/blog/a-clinic-that-keeps-its-own-records',
  },
  "two-chairs-one-diary": {
    title: { en: "Two chairs, one diary", he: "שני כיסאות, יומן אחד", ar: "كرسيّان ومفكّرة واحدة" },
    description: {
      en: "The day you take on a second pair of hands, the diary stops being a list and starts being a puzzle. Staff, rooms and equipment that the booking page actually understands.",
      he: "ביום שבו נוסף זוג ידיים שני, היומן מפסיק להיות רשימה ומתחיל להיות פאזל. עובדים, חדרים וציוד שעמוד ההזמנות באמת מבין.",
      ar: "يوم تضيف زوج يدين ثانيًا، تكفّ المفكّرة عن كونها قائمة وتصير أحجية. موظّفون وغرف ومعدّات تفهمها صفحة الحجز فعلًا.",
    },
    canonical: '/blog/two-chairs-one-diary',
  },
  "getting-paid-before-they-arrive": {
    title: { en: "Getting paid before they arrive", he: "לקבל תשלום לפני שהם מגיעים", ar: "أن تُقبَض قبل أن يصلوا" },
    description: {
      en: "Deposits, prepaid packages and the awkward conversation you never have to have. What taking money at the moment of booking changes.",
      he: "מקדמות, חבילות משולמות מראש, והשיחה הלא נעימה שלא תצטרכו לנהל. מה משתנה כשגובים כסף ברגע ההזמנה.",
      ar: "عرابين، وباقات مدفوعة مسبقًا، والحديث المحرج الذي لن تضطرّ إليه. ما الذي يغيّره قبض المال لحظة الحجز.",
    },
    canonical: '/blog/getting-paid-before-they-arrive',
  },
  "the-desk-is-a-till-too": {
    title: { en: "The desk is a till too", he: "הדלפק הוא גם קופה", ar: "طاولة الاستقبال صندوقٌ أيضًا" },
    description: {
      en: "Cash, card, part now and part later, a tip and a product on the same receipt — and a day that adds up at the end without anybody counting twice.",
      he: "מזומן, אשראי, חלק עכשיו וחלק אחר כך, טיפ ומוצר על אותה קבלה — ויום שמסתדר בסוף בלי שאף אחד סופר פעמיים.",
      ar: "نقدًا أو ببطاقة، وجزء الآن وجزء لاحقًا، وبقشيش ومنتج على الإيصال نفسه — ويوم يُغلَق في آخره دون أن يَعُدّ أحد مرّتين.",
    },
    canonical: '/blog/the-desk-is-a-till-too',
  },
  "ads-that-pay-for-themselves": {
    title: { en: "Ads that pay for themselves", he: "פרסום שמחזיר את עצמו", ar: "إعلانات تسدّد كلفتها" },
    description: {
      en: "Most small-business advertising fails for one reason: nobody can tell what it produced. What to measure, and what to fix before spending anything.",
      he: "רוב הפרסום של עסקים קטנים נכשל מסיבה אחת: אף אחד לא יודע מה הוא הניב. מה למדוד, ומה לתקן לפני שמוציאים שקל.",
      ar: "معظم إعلانات الأعمال الصغيرة تفشل لسبب واحد: لا أحد يعرف ماذا أنتجت. ما الذي تقيسه، وما الذي تُصلحه قبل أن تنفق شيئًا.",
    },
    canonical: '/blog/ads-that-pay-for-themselves',
  },
  "a-barber-shop-at-full-capacity": {
    title: { en: "A barber shop at full capacity", he: "מספרה בתפוסה מלאה", ar: "صالون حلاقة بطاقة كاملة" },
    description: {
      en: "Short appointments, walk-ins, three chairs and a queue. What a shop that runs on volume needs from a system — and what only gets in the way.",
      he: "תורים קצרים, מזדמנים, שלושה כיסאות ותור בכניסה. מה מספרה שחיה על כמות צריכה ממערכת — ומה רק מפריע.",
      ar: "مواعيد قصيرة، وزبائن بلا موعد، وثلاثة كراسٍ وطابور. ما الذي يحتاجه صالون يعيش على الكمّ من النظام — وما الذي يعترض الطريق فقط.",
    },
    canonical: '/blog/a-barber-shop-at-full-capacity',
  },
  "what-your-calendar-is-hiding": {
    title: { en: "What your calendar is hiding", he: "מה היומן שלך מסתיר", ar: "ما الذي تخفيه مفكّرتك" },
    description: {
      en: "Your diary knows which services make money, which days are worth opening and who stopped coming. It just never tells you unless you ask.",
      he: "היומן שלך יודע אילו שירותים מכניסים, אילו ימים שווה לפתוח בהם ומי הפסיק להגיע. הוא פשוט אף פעם לא אומר לך, אלא אם תשאל.",
      ar: "مفكّرتك تعرف أي الخدمات تربح، وأي الأيام تستحقّ الفتح، ومن توقّف عن المجيء. لكنها لا تخبرك أبدًا ما لم تسأل.",
    },
    canonical: '/blog/what-your-calendar-is-hiding',
  },
  "software-for-a-property-business": {
    title: { en: "Software for a property business", he: "תוכנה לעסק של נכסים", ar: "برنامج لعمل العقارات" },
    description: {
      en: "A booking tool handles the nights. Rates, deposits, each property’s own ledger, who can see what, and everything you sell besides the stay — what to ask before you choose.",
      he: "כלי הזמנות מטפל בלילות. תעריפים, מקדמות, ספר חשבונות לכל נכס, מי רואה מה, וכל מה שמוכרים מעבר לשהייה — מה לשאול לפני שבוחרים.",
      ar: "أداة الحجز تتولّى الليالي. أمّا الأسعار والعرابين ودفتر حسابات كل عقار ومن يرى ماذا وكل ما تبيعه إلى جانب الإقامة — فهذا ما ينبغي السؤال عنه قبل أن تختار.",
    },
    canonical: '/blog/software-for-a-property-business',
  },
};
